import { QueryClient, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import { useDeepCompareEffect, useShallowCompareEffect } from "react-use"

import { ECoachingChatQueryKey as EQueryKey } from "../config"
import {
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    type ICoachingChatConversationQueryDto as IConversationDto,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"

type _TSeedsParams = Pick<_TSharedParams, "entityId"> & { onComplete(areCompleted: boolean): Promise<void> }
function useSeedsCompletor({ entityId, onComplete }: _TSeedsParams): void {
    const formState: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const isNotAvailable: boolean = eq(conversationStatus, EConversationStatus.NotAvailable)

    useDeepCompareEffect((): void => {
        if (!onComplete) return void 0

        if (isNotAvailable && (formState.isFilled || formState.isSubmitted)) return void onComplete(formState.isFilled)

        return void onComplete(formState.isSubmitted)
    }, [formState.isSubmitted, formState.isFilled, isNotAvailable, onComplete])
}

type _TChatParams = Pick<_TSharedParams, "entityId"> & {
    // Light mode only reads the stamp; Full mode also needs to know whether the store considers
    // the conversation finished, because the stamp is unreliable there (see the call site).
    onComplete(completedStamp?: string | null, isConversationCompleted?: boolean): void | Promise<void>
}
function useChatCompletor({ entityId, onComplete }: _TChatParams): void {
    const qc: QueryClient = useQueryClient()

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)
    const conversationId: number | null = useConversationIdSelector(entityId)

    const shouldRunBasedOnStatus: boolean = includes<EConversationStatus>(
        [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
        conversationStatus
    )

    // `conversationStatus` is a dependency, not just `shouldRunBasedOnStatus`: both
    // GeneratingSummary and Completed satisfy the latter, so without it the callback fires once,
    // at GeneratingSummary — before the server has written `completed_stamp` — and never again.
    // Consumers that react to completion (e.g. refreshing the chapter payload) would then act on
    // a conversation the backend does not consider finished yet.
    useShallowCompareEffect((): void => {
        const queryData: IConversationDto | undefined = qc.getQueryData<IConversationDto>(
            EQueryKey.Conversation(conversationId)
        )

        if (shouldRunBasedOnStatus && !!queryData)
            void onComplete(queryData.completed_stamp, eq(conversationStatus, EConversationStatus.Completed))
    }, [shouldRunBasedOnStatus, conversationStatus, conversationId, onComplete])
}

export { useSeedsCompletor as useCoachingChatSeedsCompletor, useChatCompletor as useCoachingChatCompletor }
